import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { quotaWeightedContribution } from '../../services/resourceAdapter'

export interface HelperQuotaProjection {
  tasks_completed?: number
  sales_units?: number
  checklists_done?: number
  weighted_progress?: number
  hours_logged?: number
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

export const dayStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())

export const dateFromValue = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'object' && value && 'toDate' in value && typeof (value as { toDate: () => unknown }).toDate === 'function') {
    const fromTimestamp = (value as { toDate: () => unknown }).toDate()
    return fromTimestamp instanceof Date && !isNaN(fromTimestamp.getTime()) ? fromTimestamp : null
  }
  const parsed = new Date(String(value))
  return isNaN(parsed.getTime()) ? null : parsed
}

export const dayKey = (d: Date): string => {
  const normalized = dayStart(d)
  const month = String(normalized.getMonth() + 1).padStart(2, '0')
  const day = String(normalized.getDate()).padStart(2, '0')
  return `${normalized.getFullYear()}-${month}-${day}`
}

const isActiveWork = (item: HelperAssignedItem): boolean =>
  item.isCompleted !== true
  && item.effectiveStatus !== 'COMPLETED'
  && item.effectiveStatus !== 'NOT_REQUIRED'
  && item.effectiveStatus !== 'POSTPONED'

export const isHelperItemAssignedForDate = (item: HelperAssignedItem, date: Date = new Date()): boolean => {
  if (!isActiveWork(item)) return false
  const targetStart = dayStart(date).getTime()
  const targetEnd = targetStart + MS_PER_DAY - 1
  const start = dateFromValue(item.scheduleStartDate)
  const end = dateFromValue(item.scheduleEndDate)
  if (!start && !end) return true
  const startMs = start ? dayStart(start).getTime() : Number.NEGATIVE_INFINITY
  const endMs = end ? dayStart(end).getTime() + MS_PER_DAY - 1 : Number.POSITIVE_INFINITY
  return startMs <= targetEnd && endMs >= targetStart
}

export const buildHelperQuotaProjection = (
  items: readonly HelperAssignedItem[],
  date: Date = new Date(),
): HelperQuotaProjection => {
  const assignedToday = items.filter((item) => isHelperItemAssignedForDate(item, date))
  if (assignedToday.length === 0) return {}
  return {
    checklists_done: assignedToday.length,
    weighted_progress: assignedToday.reduce((sum, item) => sum + quotaWeightedContribution(item), 0),
  }
}
